import React, { useContext, useEffect, useRef, useState } from 'react'
import {
    Modal, ModalHeader, ModalBody, ModalFooter,
    Button, Form, FormGroup, Label, Input, FormFeedback,
    Row, Col, Alert, Spinner,
} from 'reactstrap'
import TmsLocationCreationContext from './TmsLocationCreationContext'

const ADDR_FIELDS = [
    { value: '',            label: 'N/A' },
    { value: 't_loc_id',   label: 'Location ID' },
    { value: 't_loc_nm',   label: 'Location Name' },
    { value: 'addr1',      label: 'Address 1' },
    { value: 'addr2',      label: 'Address 2' },
    { value: 'city',       label: 'City' },
    { value: 'state',      label: 'State' },
    { value: 'zip',        label: 'Zip' },
    { value: 'appt_system', label: 'Appointment System' },
]

const POLL_INTERVAL_MS = 2000
const POLL_TIMEOUT_MS  = 60000

function buildColumnMappings(mappings) {
    const result = {
        t_loc_id_field:   null,
        t_loc_nm_field:   null,
        address_fields:   [null, null, null, null, null], // [addr1, addr2, city, state, zip]
        appt_system_field: null,
    }
    Object.entries(mappings).forEach(([col, field]) => {
        if (!field) return
        if (field === 't_loc_id')     result.t_loc_id_field    = col
        else if (field === 't_loc_nm') result.t_loc_nm_field   = col
        else if (field === 'addr1')    result.address_fields[0] = col
        else if (field === 'addr2')    result.address_fields[1] = col
        else if (field === 'city')     result.address_fields[2] = col
        else if (field === 'state')    result.address_fields[3] = col
        else if (field === 'zip')      result.address_fields[4] = col
        else if (field === 'appt_system') result.appt_system_field = col
    })
    return result
}

function NewJobWizard({ onClose, onComplete }) {
    const {
        createJob, uploadFileToS3, getRecord,
        saveColumnMappings, saveApptMappings, listApptSystems,
    } = useContext(TmsLocationCreationContext)

    const [step, setStep] = useState(1)   // 1=upload, 2=column mapping, 3=appt system mapping
    const [jobName, setJobName]         = useState('')
    const [file, setFile]               = useState(null)
    const [analysisOnly, setAnalysisOnly] = useState(false)
    const [touched, setTouched]         = useState(false)
    const [errorMsg, setErrorMsg]       = useState(null)

    const [uploading, setUploading] = useState(false)
    const [polling, setPolling]     = useState(false)
    const [saving, setSaving]       = useState(false)

    const [jobId, setJobId]           = useState(null)
    const [xlsColumns, setXlsColumns] = useState([])
    const [mappings, setMappings]     = useState({})

    // Step 3 data
    const [distinctApptSystems, setDistinctApptSystems] = useState([])  // from XLS
    const [internalSystems, setInternalSystems]         = useState([])  // from DB
    const [apptMappings, setApptMappings]               = useState({})  // xlsVal → systemId

    const pollTimer = useRef(null)
    const pollStart = useRef(null)

    useEffect(() => () => clearTimeout(pollTimer.current), [])

    // ── Step 1: upload ──────────────────────────────────────────────────────

    const handleUpload = async () => {
        setTouched(true)
        if (!jobName.trim()) { setErrorMsg('Reference is required'); return }
        if (!file)           { setErrorMsg('Please select a file'); return }
        setErrorMsg(null)
        setUploading(true)
        try {
            const payload = {
                job_type:      'tms_location_creation',
                job_name:      jobName.trim(),
                file_name:     file.name,
                file_size:     file.size,
                created_by:    localStorage.getItem('email') || '',
                analysis_only: analysisOnly,
            }
            const response = await createJob(payload)
            const presignedUrl = response?.presigned_upload_url
            const newJobId     = response?.job_id
            if (!presignedUrl || !newJobId) throw new Error('Invalid response from server')

            await uploadFileToS3(presignedUrl, file)
            setJobId(newJobId)
            setUploading(false)
            setPolling(true)
            pollStart.current = Date.now()
            schedulePoll(newJobId)
        } catch (err) {
            console.error(err)
            setErrorMsg(err.message || 'Upload failed. Please try again.')
            setUploading(false)
        }
    }

    const schedulePoll = (jid) => {
        pollTimer.current = setTimeout(() => pollForColumns(jid), POLL_INTERVAL_MS)
    }

    const pollForColumns = async (jid) => {
        if (Date.now() - pollStart.current > POLL_TIMEOUT_MS) {
            setPolling(false)
            setErrorMsg('Timed out waiting for the file to be processed. Please try again.')
            return
        }
        try {
            const record = await getRecord(jid)
            if (record?.status === 'PENDING_MAPPING' && record?.xls_columns?.length > 0) {
                const cols = record.xls_columns
                setXlsColumns(cols)
                const init = {}
                cols.forEach(c => { init[c] = '' })
                setMappings(init)
                setPolling(false)
                setStep(2)
            } else if (record?.status === 'ERROR') {
                setPolling(false)
                setErrorMsg('The server could not read the uploaded file. Please check the file and try again.')
            } else {
                schedulePoll(jid)
            }
        } catch (err) {
            console.error(err)
            schedulePoll(jid)
        }
    }

    // ── Step 2: column mapping ──────────────────────────────────────────────

    const handleMappingChange = (col, value) => {
        setMappings(prev => ({ ...prev, [col]: value }))
    }

    const handleSaveColumnMappings = async () => {
        setSaving(true)
        setErrorMsg(null)
        try {
            const columnMappings = buildColumnMappings(mappings)
            const result = await saveColumnMappings(jobId, columnMappings)
            const distinct = result?.distinct_appt_systems || []
            setDistinctApptSystems(distinct)

            // Load internal appointment systems for the dropdown
            const systems = await listApptSystems() || []
            setInternalSystems(systems)

            // Initialise appt mappings — auto-default if XLS value matches an internal system_id
            const initAppt = {}
            distinct.forEach(v => {
                const match = systems.find(s => s.system_id.toLowerCase() === v.toLowerCase())
                initAppt[v] = match ? match.system_id : ''
            })
            setApptMappings(initAppt)

            setStep(3)
        } catch (err) {
            console.error(err)
            setErrorMsg(err.message || 'Failed to save column mappings. Please try again.')
        } finally {
            setSaving(false)
        }
    }

    // ── Step 3: appt system mapping ─────────────────────────────────────────

    const handleApptMappingChange = (xlsVal, internalId) => {
        setApptMappings(prev => ({ ...prev, [xlsVal]: internalId }))
    }

    const handleStartProcessing = async () => {
        setSaving(true)
        setErrorMsg(null)
        try {
            await saveApptMappings(jobId, apptMappings)
            onComplete()
        } catch (err) {
            console.error(err)
            setErrorMsg(err.message || 'Failed to start processing. Please try again.')
        } finally {
            setSaving(false)
        }
    }

    const busy = uploading || polling || saving

    const stepTitle = {
        1: 'New Upload — Step 1 of 3',
        2: 'Column Mapping — Step 2 of 3',
        3: 'Appointment System Mapping — Step 3 of 3',
    }[step]

    return (
        <Modal isOpen centered size="lg" toggle={busy ? undefined : onClose}>
            <ModalHeader toggle={busy ? undefined : onClose}>
                {stepTitle}
            </ModalHeader>

            <div style={{ opacity: saving ? 0.6 : 1, pointerEvents: saving ? 'none' : 'auto' }}>
                <ModalBody>
                    {errorMsg && <Alert color="danger">{errorMsg}</Alert>}

                    {/* ── Step 1: file + name ── */}
                    {step === 1 && (
                        <Form>
                            <FormGroup row>
                                <Label sm={3} className="col-form-label">Reference</Label>
                                <Col sm={9}>
                                    <Input
                                        type="text"
                                        value={jobName}
                                        onChange={e => { setJobName(e.target.value); setErrorMsg(null) }}
                                        placeholder="Enter a reference for this upload"
                                        invalid={touched && !jobName.trim()}
                                        disabled={busy}
                                    />
                                    <FormFeedback>Reference is required</FormFeedback>
                                </Col>
                            </FormGroup>
                            <FormGroup row>
                                <Label sm={3} className="col-form-label">File</Label>
                                <Col sm={9}>
                                    <Input
                                        type="file"
                                        accept=".xlsx,.xls"
                                        onChange={e => { setFile(e.target.files[0]); setErrorMsg(null) }}
                                        invalid={touched && !file}
                                        disabled={busy}
                                    />
                                            <FormFeedback>Please select an Excel file</FormFeedback>
                                </Col>
                            </FormGroup>
                            <div
                                className="d-flex align-items-center gap-2 p-3 rounded mt-2"
                                style={{ background: analysisOnly ? '#fff8e1' : '#f8f9fa', border: '1px solid #dee2e6' }}
                            >
                                <Input
                                    type="switch"
                                    id="analysisOnly"
                                    checked={analysisOnly}
                                    onChange={e => setAnalysisOnly(e.target.checked)}
                                    disabled={busy}
                                    style={{ cursor: 'pointer', flexShrink: 0 }}
                                />
                                <Label check for="analysisOnly" className="mb-0 fw-semibold" style={{ cursor: 'pointer' }}>
                                    Analysis only
                                    <span className="fw-normal text-muted ms-1">
                                        — preview results without updating the TMS location table
                                    </span>
                                </Label>
                            </div>
                            {uploading && (
                                <div className="d-flex align-items-center gap-2 text-muted mt-2">
                                    <Spinner size="sm" /> Uploading file...
                                </div>
                            )}
                            {polling && (
                                <div className="d-flex align-items-center gap-2 text-muted mt-2">
                                    <Spinner size="sm" /> Reading column headers from file...
                                </div>
                            )}
                        </Form>
                    )}

                    {/* ── Step 2: column mapping ── */}
                    {step === 2 && (
                        <>
                            <p className="text-muted mb-3">
                                Map each column from your file to the corresponding field.
                                Be sure to map <strong>Location ID</strong> and <strong>Appointment System</strong>.
                                Unmapped columns will be ignored.
                            </p>
                            <Row className="fw-bold border-bottom pb-2 mb-2 text-muted">
                                <Col sm={6}>XLS Column</Col>
                                <Col sm={6}>Maps To</Col>
                            </Row>
                            {xlsColumns.map(col => (
                                <Row key={col} className="mb-2 align-items-center">
                                    <Col sm={6}><span>{col}</span></Col>
                                    <Col sm={6}>
                                        <Input
                                            type="select"
                                            value={mappings[col] || ''}
                                            onChange={e => handleMappingChange(col, e.target.value)}
                                        >
                                            {ADDR_FIELDS.map(f => (
                                                <option key={f.value} value={f.value}>{f.label}</option>
                                            ))}
                                        </Input>
                                    </Col>
                                </Row>
                            ))}
                        </>
                    )}

                    {/* ── Step 3: appointment system mapping ── */}
                    {step === 3 && (
                        <>
                            <p className="text-muted mb-3">
                                Map each appointment system found in your file to the corresponding
                                internal appointment system. Unmapped systems will be used as-is.
                            </p>
                            {distinctApptSystems.length === 0 ? (
                                <Alert color="info">
                                    No appointment system values were found in the selected column.
                                    You can proceed directly to processing.
                                </Alert>
                            ) : (
                                <>
                                    <Row className="fw-bold border-bottom pb-2 mb-2 text-muted">
                                        <Col sm={6}>Value in File</Col>
                                        <Col sm={6}>Internal Appointment System</Col>
                                    </Row>
                                    {distinctApptSystems.map(xlsVal => (
                                        <Row key={xlsVal} className="mb-2 align-items-center">
                                            <Col sm={6}><span>{xlsVal}</span></Col>
                                            <Col sm={6}>
                                                <Input
                                                    type="select"
                                                    value={apptMappings[xlsVal] || ''}
                                                    onChange={e => handleApptMappingChange(xlsVal, e.target.value)}
                                                >
                                                    <option value="">— select —</option>
                                                    {internalSystems.map(s => (
                                                        <option key={s.system_id} value={s.system_id}>
                                                            {s.system_id}{s.system_nm ? ` — ${s.system_nm}` : ''}
                                                        </option>
                                                    ))}
                                                </Input>
                                            </Col>
                                        </Row>
                                    ))}
                                </>
                            )}
                        </>
                    )}
                </ModalBody>

                <ModalFooter>
                    {step === 1 && (
                        <>
                            <Button color="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
                            <Button color="primary" onClick={handleUpload} disabled={busy}>
                                {uploading ? 'Uploading...' : polling ? 'Reading file...' : 'Upload →'}
                            </Button>
                        </>
                    )}
                    {step === 2 && (
                        <>
                            <Button color="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
                            <Button color="primary" onClick={handleSaveColumnMappings} disabled={saving}>
                                {saving ? 'Loading...' : 'Next →'}
                            </Button>
                        </>
                    )}
                    {step === 3 && (
                        <>
                            <Button color="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
                            <Button color="primary" onClick={handleStartProcessing} disabled={saving}>
                                {saving ? 'Starting...' : 'Start Processing'}
                            </Button>
                        </>
                    )}
                </ModalFooter>
            </div>
        </Modal>
    )
}

export default NewJobWizard
